// =============================================================================
// Pipeline Facturation — Hub Finance > Dossiers à Facturer
// Migré vers OHGrid v1.1.1 (GROOT UI-004 stats cliquables en guise de tabs)
// =============================================================================
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  FileText, Send, CheckCircle2, Clock, AlertCircle, RefreshCw, Euro, Search,
  Eye, XCircle, Link, Loader2, ScanSearch, FileWarning, Download,
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { hasDossierAmo } from "../../utils/tenantProfile";
import { OHGrid } from "../../components/ui/oh-grid";
import PennylaneNonActif from "../../components/pennylane/PennylaneNonActif";
import { hasIntegrationPennylane } from "../../utils/tenantProfile";
import { enteteTenant } from "../../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL;

const formatMontant = (v) => {
  if (v == null || isNaN(v)) return "0,00 €";
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v);
};

// Catalogue des étapes du pipeline — mapping key → metadata
const PIPELINE_TABS_META = {
  a_facturer: { label: "À Facturer", color: "orange", icon: Euro },
  facturees: { label: "Facturées", color: "purple", icon: Send },
  payees: { label: "Payées", color: "green", icon: CheckCircle2 },
  visites_faites: { label: "Visites Faites", color: "blue", icon: Clock },
  cloturees: { label: "Clôturées", color: "gray", icon: FileText },
  abandonnees: { label: "Abandonnées", color: "red", icon: AlertCircle },
};
const PIPELINE_ORDER = ["a_facturer", "facturees", "payees", "visites_faites", "cloturees", "abandonnees"];

export default function PipelineFacturationPage({ tenantId, onNavigate }) {
  // 🔴 Entrées « Réconcilier Pennylane » : visibles SEULEMENT si le tenant DÉCLARE l’intégration
  // (feature flag `integration.pennylane`) — jamais par nom de tenant. Absent ⇒ masqué.
  const pennylaneActif = hasIntegrationPennylane();
  const [pipeline, setPipeline] = useState({});
  // PN-17 : « Retirer de la liste » demande la RAISON (avant : un confirm() puis une raison toujours identique, jamais la vraie).
  const [retrait, setRetrait] = useState(null);          // { dossierId, reference }
  const [raisonRetrait, setRaisonRetrait] = useState("");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState("a_facturer");

  // Modal réconciliation Pennylane
  const [reconcileModal, setReconcileModal] = useState({ open: false, dossier: null });
  const [reconcileSearch, setReconcileSearch] = useState("");
  const [reconcileResults, setReconcileResults] = useState([]);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [reconcileLinking, setReconcileLinking] = useState(false);

  // PENNY-CAS 3 — Transactions non justifiées
  const [unjustifiedReport, setUnjustifiedReport] = useState(null);
  const [unjustifiedLoading, setUnjustifiedLoading] = useState(false);
  const [unjustifiedModalOpen, setUnjustifiedModalOpen] = useState(false);

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem("session_token");
    return {
      // ⛔ Aucun nom de tenant en repli : sans `tenantId`, l'en-tête est omis et le
      //    serveur résout le tenant depuis la session (autorité côté serveur).
      ...(tenantId && { ...enteteTenant(tenantId) }),
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }, [tenantId]);

  // ─── Chargement (useCallback AVANT useEffect — règle anti-TDZ) ───
  const loadData = useCallback(async () => {
    if (!pennylaneActif) { setLoading(false); return; }   // ⛔ aucun appel sans intégration active
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/pennylane/cached-overview`, {
        headers: getHeaders(), credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setPipeline(data.pipeline || {});
      }
    } catch (err) { console.error("Pipeline load error:", err); }
    finally { setLoading(false); }
  }, [getHeaders, pennylaneActif]);

  useEffect(() => { loadData(); }, [loadData]);

  // ─── Flatten : tous les dossiers avec leur `_tab_key` ───
  const allDossiers = useMemo(() => {
    const out = [];
    for (const key of PIPELINE_ORDER) {
      (pipeline[key] || []).forEach(d => out.push({ ...d, _tab_key: key }));
    }
    return out;
  }, [pipeline]);

  // ─── Actions API ───
  // Fix bug Pennylane 2026-05-20 — endpoint correct: POST /create-invoice/{id}
  // (l'ancien POST /invoices retournait 405 — GET only).
  const handleCreateInvoice = useCallback(async (dossierId) => {
    setSyncing(true);
    try {
      const res = await fetch(`${API}/api/pennylane/create-invoice/${dossierId}`, {
        method: "POST", headers: getHeaders(), credentials: "include",
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        // Le backend renvoie 200 avec {success: false, error: "..."} dans
        // certains cas (facture deja creee, devis manquant, etc.).
        if (data.success === false) {
          toast.error(data.error || "Facture non créée");
        } else {
          toast.success("Facture créée avec succès");
          await loadData();
        }
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Erreur lors de la création (HTTP ${res.status})`);
      }
    } catch { toast.error("Erreur réseau"); }
    finally { setSyncing(false); }
  }, [getHeaders, loadData]);

  const handleCancelInvoiceRequest = useCallback(async (dossierId) => {
    if (!window.confirm("Annuler la demande de facturation pour ce dossier ?")) return;
    setSyncing(true);
    try {
      const res = await fetch(`${API}/api/pennylane/cancel-invoice-request/${dossierId}`, {
        method: "POST", headers: getHeaders(), credentials: "include",
      });
      if (res.ok) { toast.success("Demande de facturation annulée"); await loadData(); }
      else { const err = await res.json().catch(() => ({})); toast.error(err.detail || "Erreur"); }
    } catch { toast.error("Erreur réseau"); }
    finally { setSyncing(false); }
  }, [getHeaders, loadData]);

  const handleReconcile = useCallback(async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API}/api/pennylane/reconcile`, {
        method: "POST", headers: getHeaders(), credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        // BUG-12 fix message delta (decision JJ 2026-05-04) — afficher
        // uniquement ce qui a change cette fois.
        const updated = data.updated || 0;            // statuts MAJ phase 1
        const delinked = data.delinked_invalid || 0;  // liens invalides corriges (phase 1.5)
        const newLinks = data.auto_linked || 0;       // nouveaux liens (phase 2)
        const total = updated + delinked + newLinks;

        if (total === 0) {
          toast.info("Réconciliation : aucun changement, tout est à jour");
        } else {
          const parts = [];
          if (newLinks > 0) parts.push(`${newLinks} dossier${newLinks > 1 ? "s" : ""} nouvellement lié${newLinks > 1 ? "s" : ""}`);
          if (updated > 0) parts.push(`${updated} statut${updated > 1 ? "s" : ""} mis à jour`);
          if (delinked > 0) parts.push(`${delinked} lien${delinked > 1 ? "s" : ""} invalide${delinked > 1 ? "s" : ""} corrigé${delinked > 1 ? "s" : ""}`);
          toast.success(`Réconciliation : ${parts.join(", ")}`);
          await loadData();
        }
      } else toast.error("Erreur réconciliation");
    } catch { toast.error("Erreur réseau"); }
    finally { setSyncing(false); }
  }, [getHeaders, loadData]);

  // PENNY-CAS 3 — Export CSV pour Stéphanie (format ; séparateur + BOM UTF-8 Excel)
  const exportUnjustifiedCsv = useCallback(() => {
    if (!unjustifiedReport) return;
    const rows = [];
    const pushSection = (section, items) => {
      (items || []).forEach((it) => {
        rows.push({
          section,
          transaction_id: it.transaction_id || "",
          amount: it.amount != null ? String(it.amount).replace(".", ",") : "",
          date: it.date || "",
          label: (it.label || "").replace(/[\r\n;]/g, " "),
          client_extracted: (it.client_extracted || "").replace(/[\r\n;]/g, " "),
          dossier_ref: it.dossier_reference || "",
          pennylane_invoice: it.pennylane_invoice_number || "",
          score: it.score != null ? String(it.score).replace(".", ",") : "",
          commentaire_stephanie: "",
        });
      });
    };
    pushSection("liees_facture", unjustifiedReport.matched_to_existing_invoice);
    pushSection("dossier_sans_facture", unjustifiedReport.matched_dossier_no_invoice);
    pushSection("sans_dossier", unjustifiedReport.unmatched);

    const headers = [
      "section", "transaction_id", "amount", "date", "label",
      "client_extracted", "dossier_ref", "pennylane_invoice", "score",
      "commentaire_stephanie",
    ];
    const lines = [headers.join(";")];
    rows.forEach((r) => {
      lines.push(headers.map((h) => `"${(r[h] || "").toString().replace(/"/g, '""')}"`).join(";"));
    });
    const bom = "﻿";
    const blob = new Blob([bom + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
    const today = new Date().toISOString().slice(0, 10);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pennylane_transactions_non_justifiees_${today}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`CSV exporté (${rows.length} lignes)`);
  }, [unjustifiedReport]);

  // PENNY-CAS 3 — Analyse transactions non justifiées (dry-run par défaut)
  const handleAnalyzeUnjustified = useCallback(async ({ dryRun = true } = {}) => {
    setUnjustifiedLoading(true);
    try {
      const res = await fetch(`${API}/api/pennylane/reconcile/unjustified`, {
        method: "POST", headers: getHeaders(), credentials: "include",
        body: JSON.stringify({ dry_run: dryRun }),
      });
      if (res.ok) {
        const data = await res.json();
        setUnjustifiedReport(data);
        setUnjustifiedModalOpen(true);
        const matched = (data.matched_to_existing_invoice || []).length;
        const noInvoice = (data.matched_dossier_no_invoice || []).length;
        const unmatched = (data.unmatched || []).length;
        if (data.total_unjustified === 0) {
          toast.info("Aucune transaction non justifiée");
        } else if (!dryRun && data.annotated_count > 0) {
          toast.success(`${data.annotated_count} facture(s) annotée(s) avec transaction_id`);
        } else {
          toast.success(
            `Analyse : ${matched} liées · ${noInvoice} sans facture · ${unmatched} sans dossier`
          );
        }
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur analyse");
      }
    } catch {
      toast.error("Erreur réseau");
    } finally {
      setUnjustifiedLoading(false);
    }
  }, [getHeaders]);

  const handleRetireFacturation = useCallback((dossierId, reference) => {
    setRaisonRetrait("");
    setRetrait({ dossierId, reference });
  }, []);

  const confirmerRetrait = useCallback(async () => {
    const raison = raisonRetrait.trim();
    if (!retrait || !raison) return;
    setSyncing(true);
    try {
      const res = await fetch(`${API}/api/pennylane/retire-facturation/${retrait.dossierId}`, {
        method: "POST", headers: getHeaders(), credentials: "include",
        body: JSON.stringify({ raison }),
      });
      if (res.ok) { toast.success("Dossier retiré de la liste"); setRetrait(null); await loadData(); }
      else { const err = await res.json().catch(() => ({})); toast.error(err.detail || "Erreur"); }
    } catch { toast.error("Erreur réseau"); }
    finally { setSyncing(false); }
  }, [getHeaders, loadData, retrait, raisonRetrait]);

  const searchPennylaneInvoices = useCallback(async (query) => {
    if (!query || query.length < 2) { setReconcileResults([]); return; }
    setReconcileLoading(true);
    try {
      const res = await fetch(`${API}/api/pennylane/search-invoices?q=${encodeURIComponent(query)}&limit=15`, {
        headers: getHeaders(), credentials: "include",
      });
      if (res.ok) setReconcileResults(await res.json());
    } catch { /* noop */ }
    finally { setReconcileLoading(false); }
  }, [getHeaders]);

  const handleLinkInvoice = useCallback(async (dossierId, pennylaneId, invoiceNumber) => {
    setReconcileLinking(true);
    try {
      const res = await fetch(`${API}/api/pennylane/link-invoice/${dossierId}`, {
        method: "POST", headers: getHeaders(), credentials: "include",
        body: JSON.stringify({ pennylane_id: pennylaneId, invoice_number: invoiceNumber }),
      });
      if (res.ok) {
        toast.success("Facture liée au dossier");
        setReconcileModal({ open: false, dossier: null });
        await loadData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur de liaison");
      }
    } catch { toast.error("Erreur réseau"); }
    finally { setReconcileLinking(false); }
  }, [getHeaders, loadData]);

  // ─── Config OHGrid ───
  const stats = useMemo(() => PIPELINE_ORDER.map(key => {
    const meta = PIPELINE_TABS_META[key];
    return {
      key,
      label: meta.label,
      value: (pipeline[key] || []).length,
      icon: meta.icon,
      color: meta.color,
      filter: (row) => row._tab_key === key,
    };
  }), [pipeline]);

  const columns = useMemo(() => [
    { key: "référence", label: "Référence",
      render: (v) => <span className="text-[var(--oh-text)] font-medium text-sm">{v || "—"}</span> },
    { key: "client_nom", label: "Client",
      render: (v) => <span className="text-[var(--oh-text-secondary)] text-sm">{v || "—"}</span> },
    // LOT B — « Montant AMO » est une donnée AMO : colonne retirée si le tenant n'en déclare pas.
    ...(hasDossierAmo() ? [{ key: "montant_amo", label: "Montant AMO",
      render: (v) => <span className="text-[var(--oh-text)] font-mono text-sm">{v > 0 ? formatMontant(v) : "—"}</span> }] : []),
    { key: "invoice_number", label: "Facture", sortable: false,
      render: (v) => v
        ? <Badge className="bg-[var(--oh-stat-purple-bg)] text-[var(--oh-stat-purple-text)] text-xs">{v}</Badge>
        : <span className="text-[var(--oh-text-secondary)] text-xs">—</span> },
    { key: "statut_invoice", label: "Statut", sortable: false,
      render: (_, row) => row.is_paid
        ? <Badge className="bg-green-500/20 text-green-400 text-xs"><CheckCircle2 className="w-3 h-3 mr-1" /> Payée</Badge>
        : row.has_invoice
          ? <Badge className="bg-[var(--oh-stat-purple-bg)] text-[var(--oh-stat-purple-text)] text-xs"><Send className="w-3 h-3 mr-1" /> Facturée</Badge>
          : <span className="text-[var(--oh-text-muted)] text-xs">Non facturé</span> },
    { key: "date_echeance", label: "Échéance",
      render: (v) => v
        ? <span className="text-[var(--oh-text-secondary)] text-xs">{new Date(v).toLocaleDateString("fr-FR")}</span>
        : <span className="text-[var(--oh-text-secondary)] text-xs">—</span> },
  ], []);

  // Actions dépendent de activeTab — mises à jour quand l'utilisateur change de stat
  const rowActions = useMemo(() => [
    { label: "Ouvrir le dossier", icon: Eye,
      onClick: (row) => onNavigate && onNavigate("dossier", { id: row.id }) },
    { label: "Créer la facture", icon: Send,
      onClick: (row) => handleCreateInvoice(row.id),
      show: (row) => row._tab_key === "a_facturer" && !row.has_invoice,
      className: "text-purple-400" },
    { label: "Réconcilier avec Pennylane", icon: Link,
      onClick: (row) => {
        setReconcileModal({ open: true, dossier: row });
        setReconcileSearch(row.client_nom || "");
        searchPennylaneInvoices(row.client_nom || "");
      },
      show: (row) => pennylaneActif && row._tab_key === "a_facturer",
      className: "text-blue-400" },
    { label: "Annuler la demande", icon: XCircle,
      onClick: (row) => handleCancelInvoiceRequest(row.id),
      show: (row) => row._tab_key === "facturees" && row.has_invoice && !row.is_paid,
      className: "text-red-400" },
    { label: "Retirer de la liste", icon: XCircle,
      onClick: (row) => handleRetireFacturation(row.id, row.référence),
      show: (row) => row._tab_key === "a_facturer",
      className: "text-red-400" },
  ], [onNavigate, handleCreateInvoice, handleCancelInvoiceRequest, handleRetireFacturation, searchPennylaneInvoices, pennylaneActif]);

  const searchFilter = useCallback((row, q) => (
    (row.référence || "").toLowerCase().includes(q) ||
    (row.client_nom || "").toLowerCase().includes(q) ||
    (row.invoice_number || "").toLowerCase().includes(q)
  ), []);

  // Montant total dans la tab active
  const totalMontantActiveTab = useMemo(
    () => (pipeline[activeTab] || []).reduce((s, d) => s + (d.montant_amo || 0), 0),
    [pipeline, activeTab]
  );
  const countActiveTab = (pipeline[activeTab] || []).length;

  if (!pennylaneActif) return <PennylaneNonActif />;

  return (
    <div className="space-y-6" data-testid="pipeline-facturation-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[var(--oh-text)]">Dossiers à Facturer</h2>
          <p className="text-[var(--oh-text-muted)] text-sm mt-1">
            {allDossiers.length} dossier(s) au total{hasDossierAmo() ? ` · ${formatMontant(totalMontantActiveTab)}` : ""} dans "{PIPELINE_TABS_META[activeTab]?.label}" ({countActiveTab})
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {pennylaneActif && (
            <Button onClick={handleReconcile} disabled={syncing}
              variant="outline" style={{ background: "transparent", color: "var(--oh-accent-text)", borderColor: "var(--oh-accent)" }} data-testid="reconcile-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
              Rapprocher avec Pennylane
            </Button>
          )}
          <Button onClick={() => handleAnalyzeUnjustified({ dryRun: true })}
            disabled={unjustifiedLoading}
            variant="outline" style={{ background: "transparent", color: "var(--oh-accent-text)", borderColor: "var(--oh-accent)" }}
            data-testid="analyze-unjustified-btn"
            aria-label="Analyser transactions Pennylane non justifiées">
            <ScanSearch className={`w-4 h-4 mr-2 ${unjustifiedLoading ? "animate-spin" : ""}`} />
            Transactions non justifiées
          </Button>
        </div>
      </div>

      <OHGrid
        data={allDossiers}
        columns={columns}
        stats={stats}
        defaultActiveStat="a_facturer"
        onActiveStatChange={(key) => setActiveTab(key || "a_facturer")}
        rowActions={rowActions}
        pagination={true}
        pageSize={25}
        loading={loading}
        onRefresh={loadData}
        onRowClick={(row) => onNavigate && onNavigate("dossier", { id: row.id })}
        searchPlaceholder="Rechercher par référence, client, n° facture..."
        searchFilter={searchFilter}
        defaultSortKey="référence"
        defaultSortDir="desc"
        emptyMessage="Aucun dossier dans cette catégorie"
        testIdPrefix="pipeline"
      />

      {/* Modal Réconciliation Pennylane (inchangé) */}
      {/* PN-17 — raison obligatoire pour retirer un dossier de la liste */}
      <Dialog open={!!retrait} onOpenChange={(o) => { if (!o) setRetrait(null); }}>
        <DialogContent data-testid="retrait-dialog">
          <DialogHeader><DialogTitle>Retirer ce dossier de la liste « À facturer »</DialogTitle></DialogHeader>
          <p className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>
            Le dossier {retrait?.reference || ""} n'est pas supprimé. Dites pourquoi il n'est plus à facturer : la raison est gardée.
          </p>
          <Textarea value={raisonRetrait} onChange={(e) => setRaisonRetrait(e.target.value)} data-testid="retrait-raison"
            placeholder="Pourquoi retirer ce dossier de la liste ?" aria-label="Raison du retrait" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRetrait(null)}>Annuler</Button>
            <Button onClick={confirmerRetrait} disabled={!raisonRetrait.trim() || syncing} data-testid="retrait-confirmer"
              style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" }}>Retirer de la liste</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reconcileModal.open} onOpenChange={(open) => !open && setReconcileModal({ open: false, dossier: null })}>
        <DialogContent style={{ background: "var(--oh-bg-surface, #1a1a2e)", borderColor: "var(--oh-border, rgba(255,255,255,0.1))", maxWidth: "560px" }} data-testid="reconcile-modal">
          <DialogHeader>
            <DialogTitle className="text-[var(--oh-text)] flex items-center gap-2">
              <Link className="w-5 h-5 text-blue-400" />
              Réconcilier avec facture Pennylane
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {reconcileModal.dossier && (
              <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
                <p className="text-sm text-blue-300">
                  Dossier : <strong className="font-mono">{reconcileModal.dossier.référence}</strong>
                  <span className="text-blue-400/70 ml-2">{reconcileModal.dossier.client_nom}</span>
                  {hasDossierAmo() && <span className="text-blue-400/50 ml-2">({formatMontant(reconcileModal.dossier.montant_amo)})</span>}
                </p>
              </div>
            )}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Rechercher par client, numéro, montant..."
                value={reconcileSearch}
                onChange={(e) => { setReconcileSearch(e.target.value); searchPennylaneInvoices(e.target.value); }}
                className="pl-10"
                style={{ background: "var(--oh-bg-elevated, #252540)", borderColor: "var(--oh-border)", color: "white" }}
                data-testid="reconcile-search-input"
              />
              {reconcileLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-zinc-500" />}
            </div>
            <div className="max-h-64 overflow-y-auto rounded-lg" style={{ border: "1px solid var(--oh-border, rgba(255,255,255,0.1))" }}>
              {reconcileResults.length === 0 ? (
                <div className="p-6 text-center text-zinc-500 text-sm">
                  {reconcileSearch.length >= 2 ? "Aucune facture trouvée" : "Tapez au moins 2 caractères"}
                </div>
              ) : reconcileResults.map((inv, i) => {
                const alreadyLinked = !!inv.dossier_id;
                return (
                  <div key={inv.pennylane_id || i}
                    className={`p-3 flex items-center justify-between transition-colors ${alreadyLinked ? "opacity-40" : "hover:bg-zinc-800/50 cursor-pointer"}`}
                    style={{ borderBottom: "1px solid var(--oh-border, rgba(255,255,255,0.05))" }}
                    onClick={() => !alreadyLinked && !reconcileLinking && handleLinkInvoice(reconcileModal.dossier.id, inv.pennylane_id, inv.numero)}
                    data-testid={`reconcile-invoice-${inv.pennylane_id}`}>
                    <div>
                      <p className="text-sm text-[var(--oh-text)] font-mono">{inv.numero || "—"}</p>
                      <p className="text-xs text-zinc-400">
                        {inv.client_name || inv.client_nom || "—"}
                        {inv.date && <span className="ml-2">{new Date(inv.date).toLocaleDateString("fr-FR")}</span>}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-[var(--oh-text)]">{formatMontant(inv.montant_ttc || inv.montant_ht || 0)}</p>
                      {alreadyLinked
                        ? <p className="text-[10px] text-amber-400">Déjà liée</p>
                        : <Badge className="bg-blue-500/15 text-blue-400 text-[10px]">Sélectionner</Badge>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReconcileModal({ open: false, dossier: null })}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PENNY-CAS 3 — Modal Transactions non justifiées */}
      <Dialog open={unjustifiedModalOpen} onOpenChange={setUnjustifiedModalOpen}>
        <DialogContent
          style={{ background: "var(--oh-bg-surface, #1a1a2e)", borderColor: "var(--oh-border, rgba(255,255,255,0.1))", maxWidth: "880px" }}
          data-testid="unjustified-modal">
          <DialogHeader>
            <DialogTitle className="text-[var(--oh-text)] flex items-center gap-2">
              <FileWarning className="w-5 h-5 text-amber-400" />
              Transactions Pennylane non justifiées
            </DialogTitle>
          </DialogHeader>
          {unjustifiedReport && (
            <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto">
              {/* Stats résumé */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div className="p-2 rounded bg-amber-500/10 border border-amber-500/30">
                  <div className="text-amber-300 font-semibold text-lg">{unjustifiedReport.total_unjustified}</div>
                  <div className="text-amber-400/70">Non justifiées</div>
                </div>
                <div className="p-2 rounded bg-zinc-500/10 border border-zinc-500/30">
                  <div className="text-zinc-300 font-semibold text-lg">{unjustifiedReport.excluded_anah}</div>
                  <div className="text-zinc-400/70">Exclues ANAH</div>
                </div>
                <div className="p-2 rounded bg-blue-500/10 border border-blue-500/30">
                  <div className="text-blue-300 font-semibold text-lg">{(unjustifiedReport.matched_to_existing_invoice || []).length}</div>
                  <div className="text-blue-400/70">Liées facture</div>
                </div>
                <div className="p-2 rounded bg-orange-500/10 border border-orange-500/30">
                  <div className="text-orange-300 font-semibold text-lg">{(unjustifiedReport.matched_dossier_no_invoice || []).length}</div>
                  <div className="text-orange-400/70">Dossier sans facture</div>
                </div>
              </div>

              {/* Section : matched to existing invoice */}
              {(unjustifiedReport.matched_to_existing_invoice || []).length > 0 && (
                <UnjustifiedSection
                  title="Transactions rattachées à une facture déjà dans OmniHub"
                  color="blue"
                  items={unjustifiedReport.matched_to_existing_invoice}
                  showInvoice
                />
              )}

              {/* Section : matched dossier no invoice */}
              {(unjustifiedReport.matched_dossier_no_invoice || []).length > 0 && (
                <UnjustifiedSection
                  title="Dossier rattaché, sans facture (à créer à la main)"
                  color="orange"
                  items={unjustifiedReport.matched_dossier_no_invoice}
                />
              )}

              {/* Section : unmatched */}
              {(unjustifiedReport.unmatched || []).length > 0 && (
                <UnjustifiedSection
                  title="Sans dossier rattaché"
                  color="red"
                  items={unjustifiedReport.unmatched}
                />
              )}

              {/* Note bloqueur API */}
              <div className="p-2 rounded bg-zinc-500/5 border border-zinc-500/20 text-xs text-zinc-400">
                ⚠ Pennylane ne permet pas encore cette action : le dernier clic de rapprochement
                reste à faire à la main dans Pennylane. OmniHub peut au moins noter
                l'identifiant de la transaction sur la facture (bouton ci-dessous).
              </div>
            </div>
          )}
          <DialogFooter className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setUnjustifiedModalOpen(false)}>Fermer</Button>
            {unjustifiedReport && unjustifiedReport.total_unjustified > 0 && (
              <Button
                onClick={exportUnjustifiedCsv}
                variant="outline"
                data-testid="export-unjustified-csv-btn"
                aria-label="Exporter le rapport CSV pour Stéphanie">
                <Download className="w-4 h-4 mr-2" />
                Exporter CSV
              </Button>
            )}
            {unjustifiedReport && unjustifiedReport.dry_run && (unjustifiedReport.matched_to_existing_invoice || []).length > 0 && (
              <Button
                onClick={() => handleAnalyzeUnjustified({ dryRun: false })}
                disabled={unjustifiedLoading}
                style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" }}
                data-testid="annotate-matched-btn">
                {unjustifiedLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Annoter les factures rattachées
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UnjustifiedSection({ title, color, items, showInvoice = false }) {
  const colorMap = {
    blue: { bg: "bg-blue-500/5", border: "border-blue-500/20", text: "text-blue-300" },
    orange: { bg: "bg-orange-500/5", border: "border-orange-500/20", text: "text-orange-300" },
    red: { bg: "bg-red-500/5", border: "border-red-500/20", text: "text-red-300" },
  };
  const c = colorMap[color] || colorMap.blue;
  const fmt = (v) => v == null || isNaN(v) ? "—" : new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v);
  return (
    <div className={`rounded ${c.bg} border ${c.border} p-3`}>
      <h4 className={`text-sm font-semibold ${c.text} mb-2`}>{title} ({items.length})</h4>
      <div className="space-y-1 max-h-48 overflow-y-auto">
        {items.map((it, i) => (
          <div key={it.transaction_id || i} className="text-xs py-1 px-2 rounded bg-black/20 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
            <div className="flex-1 min-w-0">
              <span className="text-[var(--oh-text)] font-mono">{fmt(it.amount)}</span>
              {it.date && <span className="text-zinc-500 ml-2">{new Date(it.date).toLocaleDateString("fr-FR")}</span>}
              {it.dossier_reference && <span className="text-zinc-300 ml-2">→ {it.dossier_reference}</span>}
              {showInvoice && it.pennylane_invoice_number && <span className="text-purple-300 ml-2">{it.pennylane_invoice_number}</span>}
            </div>
            <div className="text-zinc-400 truncate" title={it.client_extracted || it.label}>
              {it.client_extracted || it.label || "—"}
              {it.score > 0 && <span className="text-zinc-500 ml-2">({Math.round(it.score * 100)}%)</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
